import { useContext, useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import "@/App.css";
import { translations, detectLanguage, LanguageContext, SUPPORTED } from "@/i18n";
import Seo from "@/components/Seo";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Vision from "@/components/Vision";
import Services from "@/components/Services";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import BlogList from "@/components/BlogList";
import BlogPost from "@/components/BlogPost";
import { Toaster } from "@/components/ui/sonner";

function DetectRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    let mounted = true;
    detectLanguage().then((l) => {
      if (mounted) navigate(`/${l}`, { replace: true });
    });
    return () => { mounted = false; };
  }, [navigate]);
  return <div className="bg-[#0A0A0A] min-h-screen" />;
}

function Shell() {
  const { lang: langParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = SUPPORTED.includes(langParam) ? langParam : null;

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    if (lang) {
      try { localStorage.setItem("dadabio_lang", lang); } catch (e) { /* ignore */ }
    }
  }, [lang]);

  if (!lang) return <Navigate to="/en" replace />;

  const setLang = (l) => {
    const rest = location.pathname.replace(/^\/[a-z]{2}/i, "");
    navigate(`/${l}${rest}`);
  };
  const t = translations[lang];

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      <div className="bg-[#0A0A0A] text-white min-h-screen font-body antialiased selection:bg-[#00F0FF] selection:text-black">
        <Navbar />
        <Outlet />
        <Footer />
        <Toaster theme="dark" position="bottom-center" />
      </div>
    </LanguageContext.Provider>
  );
}

function Home() {
  const { lang, t } = useContext(LanguageContext);
  return (
    <main>
      <Seo lang={lang} t={t} />
      <Hero />
      <Marquee />
      <Vision />
      <Services />
      <Contact />
    </main>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<DetectRedirect />} />
        <Route path="/:lang" element={<Shell />}>
          <Route index element={<Home />} />
          <Route path="blog" element={<BlogList />} />
          <Route path="blog/:slug" element={<BlogPost />} />
        </Route>
        <Route path="*" element={<DetectRedirect />} />
      </Routes>
    </BrowserRouter>
  );
}
